/**
 * City-name comparison, mirroring the backend's routing rule.
 *
 * City is free text on both a warehouse row and a client address, so "Bangalore", "bengaluru" and
 * " Bengaluru " all name one place. The backend normalises the same way before it decides whether a
 * leg is intracity (`oms/allocation/services/fulfilment.py::_norm_city`), so anything the UI warns
 * about here matches what the router will actually do with the order.
 *
 * Keep the alias table in step with the backend's `_CITY_ALIASES`.
 */
const CITY_ALIASES: Record<string, string> = {
  bangalore: "bengaluru",
  bengaluru: "bengaluru",
  secunderabad: "hyderabad",
  hyderabad: "hyderabad",
};

/** Lowercased, whitespace-collapsed and alias-resolved. "" when there is no usable city. */
export const normalizeCity = (city: unknown): string => {
  if (!city) return "";
  const c = String(city).trim().split(/\s+/).join(" ").toLowerCase();
  return CITY_ALIASES[c] ?? c;
};

/**
 * True only when BOTH cities are known and they differ. An unknown city on either side returns
 * false: we would rather stay quiet than fire a warning off a blank field, since an operator who
 * learns to dismiss the popup stops reading it.
 */
export const isCrossCity = (a: unknown, b: unknown): boolean => {
  const x = normalizeCity(a);
  const y = normalizeCity(b);
  return !!x && !!y && x !== y;
};

/** Title-cased for display — the raw value is whatever someone typed into the address form. */
export const cityLabel = (city: unknown): string => {
  const c = normalizeCity(city);
  return c ? c.charAt(0).toUpperCase() + c.slice(1) : "";
};
